<template>
  <div class="task-categorization-chart">
    <a-card title="Operations Categorization" :loading="loading">
      <highcharts :options="chartOptions" />
    </a-card>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue';
import Highcharts from 'highcharts';
import sunburst from 'highcharts/modules/sunburst';

// Initialize sunburst module
if (typeof Highcharts === 'object') {
  sunburst(Highcharts);
}

export default {
  name: 'TaskCategorizationChart',
  components: {
    highcharts: Chart,
  },
  props: {
    categorizationData: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Sample data matching API response structure
      sampleData: {
        Electric: {
          tasks: [
            {
              task_name: 'Battery Assembly',
              total_cycles: 85,
              good_cycles: 75.0,
              bad_cycles: 10.0,
              avg_cycle_time: 210.5,
              total_work_time: 17892.5,
              avg_inference_time: 45.2,
            },
            {
              task_name: 'Motor Installation',
              total_cycles: 82,
              good_cycles: 78.0,
              bad_cycles: 4.0,
              avg_cycle_time: 195.75,
              total_work_time: 16051.5,
              avg_inference_time: 38.9,
            },
          ],
          total_cycles: 167,
          avg_cycle_time: 203.23,
          total_work_time: 33944.0,
        },
        Hybrid: {
          tasks: [
            {
              task_name: 'Engine Assembly',
              total_cycles: 65,
              good_cycles: 58.0,
              bad_cycles: 7.0,
              avg_cycle_time: 245.5,
              total_work_time: 15957.5,
              avg_inference_time: 52.3,
            },
            {
              task_name: 'Battery Integration',
              total_cycles: 63,
              good_cycles: 55.0,
              bad_cycles: 8.0,
              avg_cycle_time: 220.3,
              total_work_time: 13878.9,
              avg_inference_time: 48.75,
            },
          ],
          total_cycles: 128,
          avg_cycle_time: 233.1,
          total_work_time: 29836.4,
        },
        Fuel: {
          tasks: [
            {
              task_name: 'Engine Assembly',
              total_cycles: 95,
              good_cycles: 88.0,
              bad_cycles: 7.0,
              avg_cycle_time: 235.8,
              total_work_time: 22401.0,
              avg_inference_time: 49.6,
            },
            {
              task_name: 'Transmission Install',
              total_cycles: 92,
              good_cycles: 85.0,
              bad_cycles: 7.0,
              avg_cycle_time: 280.45,
              total_work_time: 25801.4,
              avg_inference_time: 55.2,
            },
          ],
          total_cycles: 187,
          avg_cycle_time: 257.77,
          total_work_time: 48202.4,
        },
      },
    };
  },
  computed: {
    apiData() {
      return Object.keys(this.categorizationData).length > 0
        ? this.categorizationData
        : {};
    },
    chartData() {
      return this.convertToSunburstFormat(this.apiData);
    },
    chartOptions() {
      return {
        chart: {
          type: 'sunburst',
          height: 600,
          backgroundColor: 'transparent',
          zoomType: 'xy',
        },
        title: {
          text: null,
          style: {
            fontSize: '16px',
            fontWeight: 'bold',
          },
        },
        subtitle: {
          text: 'Category → Task → Total Cycles → Good/Bad Cycles',
          style: {
            fontSize: '12px',
            color: '#666',
          },
        },
        series: [
          {
            type: 'sunburst',
            data: this.chartData,
            allowDrillToNode: true,
            cursor: 'pointer',
            dataLabels: {
              format: '{point.name}',
              filter: {
                property: 'innerArcLength',
                operator: '>',
                value: 16,
              },
              style: {
                fontSize: '11px',
                fontWeight: 'normal',
                textOutline: 'none',
              },
            },
            levels: [
              {
                level: 1,
                levelIsConstant: false,
                dataLabels: {
                  filter: {
                    property: 'outerArcLength',
                    operator: '>',
                    value: 64,
                  },
                  style: {
                    fontSize: '13px',
                    fontWeight: 'bold',
                  },
                },
                colorByPoint: true,
                colors: ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de'],
              },
              {
                level: 2,
                colorVariation: {
                  key: 'brightness',
                  to: -0.3,
                },
                dataLabels: {
                  style: {
                    fontSize: '11px',
                  },
                },
              },
              {
                level: 3,
                colorVariation: {
                  key: 'brightness',
                  to: -0.5,
                },
                dataLabels: {
                  style: {
                    fontSize: '10px',
                    fontWeight: 'normal',
                  },
                },
              },
              {
                level: 4,
                colorByPoint: false,
                dataLabels: {
                  style: {
                    fontSize: '9px',
                    fontWeight: 'normal',
                  },
                },
              },
            ],
          },
        ],
        tooltip: {
          headerFormat: '',
          pointFormat:
            '<b>{point.name}</b><br/>Value: <b>{point.value}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>',
        },
        credits: {
          enabled: false,
        },
      };
    },
  },
  methods: {
    convertToSunburstFormat(apiData) {
      const flatData = [];
      let categoryIndex = 0;

      Object.entries(apiData).forEach(([categoryName, categoryData]) => {
        const categoryId = `0.${categoryIndex}`;

        // Add category node (Level 1)
        flatData.push({
          id: categoryId,
          parent: '',
          name: categoryName,
          value: categoryData.total_cycles,
        });

        // Add tasks within this category (Level 2)
        categoryData.tasks.forEach((task, taskIndex) => {
          const taskId = `1.${categoryIndex * 100 + taskIndex}`;

          // Add task node
          flatData.push({
            id: taskId,
            parent: categoryId,
            name: task.task_name,
            value: task.total_cycles,
          });

          // Add Total Cycles node (Level 3)
          const totalCyclesId = `${taskId}.total`;
          flatData.push({
            id: totalCyclesId,
            parent: taskId,
            name: `Total Cycles: ${task.total_cycles}`,
            value: task.total_cycles,
          });

          // Add good and bad cycles as child nodes of Total Cycles (Level 4)
          if (task.good_cycles !== undefined && task.good_cycles > 0) {
            flatData.push({
              id: `${totalCyclesId}.0`,
              parent: totalCyclesId,
              name: `Good: ${task.good_cycles}`,
              value: task.good_cycles,
              color: '#91cc75', // Green color for good cycles
            });
          }

          if (task.bad_cycles !== undefined && task.bad_cycles > 0) {
            flatData.push({
              id: `${totalCyclesId}.1`,
              parent: totalCyclesId,
              name: `Bad: ${task.bad_cycles}`,
              value: task.bad_cycles,
              color: '#ee6666', // Red color for bad cycles
            });
          }
        });

        categoryIndex++;
      });

      return flatData;
    },
  },
};
</script>

<style scoped>
.task-categorization-chart {
  margin-bottom: 24px;
}

.task-categorization-chart :deep(.ant-card) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  transition: box-shadow 0.3s ease;
}

.task-categorization-chart :deep(.ant-card:hover) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.chart-description {
  color: #666;
  font-size: 13px;
  margin-bottom: 16px;
  font-style: italic;
}
</style>
